import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import { faPlus } from '@fortawesome/pro-light-svg-icons/faPlus';
import { AddSongStep } from 'fym-shared/src/music24/catalogAddSong';
import { MUSIC24_MONTHLY_PRICE_USD } from 'fym-shared/src/music24/music24Plans';
import { CatalogItemKind } from 'fym-shared/src/smartlinks/catalog';
import { BioLinkAutoAddResult } from 'fym-shared/src/smartlinks/bioLinkSettings';
import { TypedMemo } from '../../../../common/types';
import { SMLButton } from '../../../../common/views/SMLButton/SMLButton';
import { useGetCatalogQuery, useGetReleasesQuery } from '../../../../../redux/api/fymAPI';
import { CatalogItem } from '../../../../../redux/api/fymAPI.types';
import { useSMLRouter } from '../../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../../routing/sml/SMLRoutes';
import { useMusic24Capabilities } from '../../../../music24/hooks/useMusic24Capabilities';
import { useOpenSmartLinkSheetForSong } from '../../../creationSheet/useOpenSmartLinkSheetForSong';
import { useOpenPresaveWizardForSong } from '../../../wizards/presaveLink/useOpenPresaveWizardForSong';
import { usePresaveLockLabel } from '../../../presave/usePresaveLockLabel';
import { useOpenSongPage } from '../../song/page/useOpenSongPage';
import { addSongFlowMessages } from '../addSongFlowMessages';
import { addSongFactsOf, dayOf } from '../addSongFlowSteps';
import { StepHeader } from '../StepHeader';
import { usePublishedPagesUsage } from '../../../usePublishedPagesUsage';
import { DoneLead, DoneNextCards } from './DoneNextCards';
import { otherActiveRelease, pagesMeterOf } from './doneFreeLimits';
import { DoneBioLinkRow } from './DoneBioLinkRow';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole page; each part uses its own slice of it */
import styles from '../CatalogAddSong.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface DoneStepProps {
  accountId: number;
  item: CatalogItem;
  today: string;
  /** What the Bio Link auto add did (the create answer of step 1); undefined after a reload. */
  bioLinkResult?: BioLinkAutoAddResult;
  onOpenStep: (step: AddSongStep) => void;
  onAddAnother: () => void;
  onClose: () => void;
  closeLabel: string;
}

/**
 * Done (design catalog-add-song f1-f3): the full card and the three next actions. An upcoming song
 * with a date leads with the pre-save page, a released song with a SmartLink, a song with no date
 * with the release kit. The parts still missing are chips that open their step. On Free (design f3)
 * the pre-save and SmartLink cards show the published pages meter, and the kit card names the other
 * active release. The server limits (402) stay the rule.
 */
const DoneStepComponent: React.FC<DoneStepProps> = ({
  accountId,
  item,
  today,
  bioLinkResult,
  onOpenStep,
  onAddAnother,
  onClose,
  closeLabel,
}) => {
  const intl = useIntl();
  const router = useSMLRouter();
  const capabilities = useMusic24Capabilities();
  const openPresave = useOpenPresaveWizardForSong();
  const presaveLock = usePresaveLockLabel();
  const openSmartLink = useOpenSmartLinkSheetForSong();
  const openSongPage = useOpenSongPage();
  const facts = addSongFactsOf(item);
  const pages = usePublishedPagesUsage(accountId);
  const pagesMeter = pagesMeterOf(pages.limit, pages.used);
  const hasOneRelease = capabilities.activeReleasesLimit === 1;
  const releases = useGetReleasesQuery({ accountId }, { skip: !hasOneRelease });
  const catalog = useGetCatalogQuery({ accountId }, { skip: !hasOneRelease });
  const other = useMemo(() => {
    if (releases.data === undefined || catalog.data === undefined) {
      return undefined;
    }
    const titles = new Map(catalog.data.items.map((one) => [one.uuid, one.title]));
    return otherActiveRelease(releases.data.releases, titles, item.uuid, new Date());
  }, [catalog.data, item.uuid, releases.data]);
  let kitNote: string | undefined;
  if (hasOneRelease) {
    kitNote =
      other === undefined
        ? intl.formatMessage(addSongFlowMessages.doneKitFree)
        : intl.formatMessage(addSongFlowMessages.doneKitFreeOther, {
            title: other.title,
            date: intl.formatDate(other.activeUntil, { day: 'numeric', month: 'short' }),
            price: intl.formatNumber(MUSIC24_MONTHLY_PRICE_USD.artist, {
              style: 'currency',
              currency: 'USD',
              maximumFractionDigits: 0,
            }),
          });
  }
  const isOut = item.kind === CatalogItemKind.claimed;
  const isUpcoming = !isOut && item.releaseDate !== null && dayOf(item.releaseDate) >= today;

  let lead: DoneLead = 'kit';
  let sub = intl.formatMessage(addSongFlowMessages.doneSubNoDate);
  if (isOut) {
    lead = 'smartLink';
    sub = intl.formatMessage(addSongFlowMessages.doneSubReleased);
  } else if (isUpcoming) {
    lead = 'presave';
    sub = intl.formatMessage(addSongFlowMessages.doneSubPresave);
  }

  const missing = useMemo(() => {
    const list: { step: AddSongStep; label: string }[] = [];
    if (!facts.hasCover) {
      list.push({ step: 'cover', label: intl.formatMessage(addSongFlowMessages.cardCover) });
    }
    if (!facts.hasMaster) {
      list.push({ step: 'audio', label: intl.formatMessage(addSongFlowMessages.cardAudio) });
    }
    if (!facts.hasLyrics) {
      list.push({ step: 'lyrics', label: intl.formatMessage(addSongFlowMessages.cardLyrics) });
    }
    return list;
  }, [facts.hasCover, facts.hasLyrics, facts.hasMaster, intl]);

  const onPresave = useCallback(() => openPresave(item.uuid), [item.uuid, openPresave]);
  // The SmartLink that the add-song flow makes is published at once (owner decision 6, 2026-10-20).
  const onSmartLink = useCallback(() => openSmartLink(item.uuid, { from: 'addSong' }), [item.uuid, openSmartLink]);
  const onKit = useCallback(
    () => router.push(SMLRoutes.promoteStudio, { catalogItemUuid: item.uuid }),
    [item.uuid, router]
  );
  const onSongPage = useCallback(() => openSongPage(item.uuid), [item.uuid, openSongPage]);

  return (
    <>
      <span className={styles.bigOk}>
        <FontAwesomeIcon icon={faCheck} />
      </span>
      <StepHeader question={intl.formatMessage(addSongFlowMessages.doneQuestion, { title: item.title })} sub={sub} />
      <DoneBioLinkRow
        accountId={accountId}
        itemUuid={item.uuid}
        title={item.title}
        isOut={isOut || (item.releaseDate !== null && dayOf(item.releaseDate) < today)}
        result={bioLinkResult}
      />
      <DoneNextCards
        title={item.title}
        lead={lead}
        kitNote={kitNote}
        pagesMeter={pagesMeter}
        presaveLock={presaveLock}
        onPresave={onPresave}
        onSmartLink={onSmartLink}
        onKit={onKit}
        onSongPage={onSongPage}
      />
      {missing.length > 0 && (
        <div className={styles.missing}>
          <span>{intl.formatMessage(addSongFlowMessages.doneMissing)}</span>
          {missing.map((part) => (
            <button key={part.step} type="button" className={styles.chip} onClick={() => onOpenStep(part.step)}>
              <FontAwesomeIcon icon={faPlus} />
              {part.label}
            </button>
          ))}
        </div>
      )}
      <div className={styles.actions}>
        <span className={styles.actionsNote}>
          {intl.formatMessage(addSongFlowMessages.doneFind, { title: item.title })}
        </span>
        <SMLButton outline omitCap onClick={onAddAnother} testID="add-song-another">
          {intl.formatMessage(addSongFlowMessages.doneAnother)}
        </SMLButton>
        <SMLButton omitCap onClick={onClose} testID="add-song-done-close">
          {closeLabel}
        </SMLButton>
      </div>
    </>
  );
};

export const DoneStep = TypedMemo(DoneStepComponent);
